import clsx from "clsx";
import {
  CalendarDaysIcon,
  EllipsisIcon,
  NotebookPenIcon,
  Trash2Icon,
  UsersIcon,
} from "lucide-react";

import { JobNoteSheet } from "./JobNoteSheet";
import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useSelectedResourceIdFromUrl } from "@/hooks/useSelectedResourceIdFromUrl";
import { useDeleteJobNote } from "@/hooks/mutations/useDeleteJobNote";
import { useGetUser } from "@/hooks/queries/useGetUser";
import { useRemoveJobNote } from "@/hooks/sets/useRemoveJobNote";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { JobNote } from "@/types";

// TODO: STF-396: Factor out into reusable component.
const CreatedBy = ({ userId }: { userId: number }) => {
  const { data: user, status } = useGetUser({ userId });

  if (status === "pending") {
    // TODO: Loading state.
    return null;
  } else if (status === "error") {
    // TODO: Error state.
    return null;
  }

  return (
    <Text>
      {user.firstName} {user.lastName}
    </Text>
  );
};

const DropdownActions = ({ jobNoteId }: { jobNoteId: number }) => {
  const { mutate: deleteJobNote } = useDeleteJobNote();
  const removeJobNote = useRemoveJobNote();

  const handleDeleteJobNote = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent the row from being clicked.
    e.stopPropagation();

    mixpanelUtils.trackButtonClick("Delete note", MIXPANEL_SCREENS.JOB);
    deleteJobNote(
      { jobNoteId },
      {
        onSuccess: () => {
          removeJobNote({ jobNoteId });
        },
      },
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost">
          <EllipsisIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          className="text-red-500 hover:!text-red-500"
          onClick={handleDeleteJobNote}
        >
          <Trash2Icon className="h-4 w-4" />
          Delete note
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const JobNotesTable = ({
  className,
  jobNotes,
}: {
  className?: string;
  jobNotes: JobNote[];
}) => {
  const {
    selectedResourceId: selectedJobNoteId,
    setSelectedResourceId: setSelectedJobNoteId,
  } = useSelectedResourceIdFromUrl({
    paramName: "n",
  });

  // Find the selected job note based on the ID from URL.
  const selectedJobNote = selectedJobNoteId
    ? (jobNotes.find((note) => note.id === selectedJobNoteId) ?? null)
    : null;

  return (
    <Column className={clsx("overflow-auto", className)}>
      <div className="rounded-t-lg border border-b-0 border-slate-200">
        <Table>
          <TableHeader>
            <TableRow className="border-none">
              <TableHead className="w-[32%]">
                <Row className="items-center gap-1">
                  <NotebookPenIcon className="h-4 w-4 text-slate-700" />
                  <Text variant="header">Title</Text>
                </Row>
              </TableHead>
              <TableHead className="w-[20%]">
                <Row className="items-center gap-1">
                  <UsersIcon className="h-4 w-4 text-slate-700" />
                  <Text variant="header">Created By</Text>
                </Row>
              </TableHead>
              <TableHead className="w-[20%]">
                <Row className="items-center gap-1">
                  <CalendarDaysIcon className="h-4 w-4 text-slate-700" />
                  <Text variant="header">Creation Date</Text>
                </Row>
              </TableHead>
              <TableHead className="w-[20%]">
                <Row className="items-center gap-1">
                  <CalendarDaysIcon className="h-4 w-4 text-slate-700" />
                  <Text variant="header">Last Modified</Text>
                </Row>
              </TableHead>
              <TableHead className="w-[8%]" />
            </TableRow>
          </TableHeader>
        </Table>
      </div>
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        <Table>
          <TableBody>
            {jobNotes.map((jobNote: JobNote) => {
              return (
                <TableRow
                  key={jobNote.id}
                  onClick={() => {
                    mixpanelUtils.trackRowClick("Job note", MIXPANEL_SCREENS.JOB);
                    setSelectedJobNoteId(jobNote.id);
                  }}
                >
                  <TableCell className="w-[32%]">
                    <Text>{jobNote.title}</Text>
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <CreatedBy userId={jobNote.userId} />
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <Text>
                      {new Intl.DateTimeFormat("en-US").format(
                        new Date(jobNote.createdAt),
                      )}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <Text>
                      {new Intl.DateTimeFormat("en-US").format(
                        new Date(jobNote.updatedAt),
                      )}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[8%]">
                    <DropdownActions jobNoteId={jobNote.id} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <JobNoteSheet
        jobNote={selectedJobNote}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedJobNoteId(null);
          }
        }}
        open={!!selectedJobNoteId}
      />
    </Column>
  );
};
